/**
 * Alshallal Pastry Company HR System - System Layout Page
 * Phase 24.9: System administration with nested navigation
 * 
 * Access:
 *  - Super Admin: full access (all tools including destructive System Reset)
 *  - HR Manager: full parity with Super Admin except System Reset (purge)
 *  - HR Admin: limited — audit logs only
 */
import React from 'react';
import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  Shield, 
  AlertTriangle,
  Activity,
  ShieldCheck,
  Gauge,
  Trash2,
  Mail,
  FileSearch,
  HardDrive,
  Smartphone,
  FileCheck2,
  Hash
} from 'lucide-react';
import { cn } from '@/lib/utils';

const SystemPage = () => {
  const { hasRole } = useAuth();
  const { isRTL } = useLanguage();
  const location = useLocation();
  
  // RBAC, 2026-06 matrix:
  //  - Super Admin: everything
  //  - IT INF: the system side (diagnostics, monitoring, storage, email health)
  //  - HR Manager: audit log and account administration ONLY - the system and
  //    settings interface is no longer theirs
  //  - HR Admin: audit log only
  const isSuperAdmin = hasRole('super_admin');
  const isITInf = hasRole('it_inf');
  const isHRManager = hasRole('hr_manager');
  const isHRAdmin = hasRole('hr_admin');
  const canAccessSystem = isSuperAdmin || isITInf || isHRManager || isHRAdmin;
  
  if (!canAccessSystem) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">
          {isRTL ? 'غير مصرح' : 'Access Denied'}
        </h2>
        <p className="text-slate-600">
          {isRTL 
            ? 'ليس لديك صلاحية للوصول إلى إدارة النظام'
            : 'You do not have permission to access system administration'}
        </p>
      </div>
    );
  }
  
  // If at /system root, redirect to audit (first available item)
  if (location.pathname === '/system') {
    return <Navigate to="/system/audit" replace />;
  }
  
  const menuItems = [
    {
      path: '/system/audit',
      icon: Activity,
      label: { en: 'Audit Logs', ar: 'سجلات التدقيق' },
      description: { en: 'View system audit trail', ar: 'عرض سجل تدقيق النظام' },
      visible: isSuperAdmin || isITInf || isHRManager || isHRAdmin  // audit is oversight, not system
    },
    {
      path: '/system/email-health',
      icon: Mail,
      label: { en: 'Email Health', ar: 'صحة البريد' },
      description: { en: 'Monitor email delivery', ar: 'مراقبة تسليم البريد' },
      visible: isSuperAdmin || isITInf || isHRManager
    },
    {
      path: '/system/admin',
      icon: ShieldCheck,
      label: { en: 'Admin Console', ar: 'لوحة تحكم المسؤول' },
      description: { en: 'System administration tools', ar: 'أدوات إدارة النظام' },
      visible: isSuperAdmin || isITInf || isHRManager
    },
    {
      path: '/system/monitoring',
      icon: Gauge,
      label: { en: 'Monitoring', ar: 'لوحة المراقبة' },
      description: { en: 'System health & performance', ar: 'صحة النظام والأداء' },
      visible: isSuperAdmin || isITInf
    },
    {
      path: '/system/ocr-analytics',
      icon: FileSearch,
      label: { en: 'OCR Analytics', ar: 'تحليلات الاستخراج' },
      description: {
        en: 'Document extraction KPIs & stability bar',
        ar: 'مؤشرات استخراج المستندات ومعدّل الاستقرار'
      },
      // Employee-document data quality, so it stays with HR
      visible: isSuperAdmin || isHRManager
    },
    {
      path: '/system/storage-health',
      icon: HardDrive,
      label: { en: 'Storage Health', ar: 'صحة التخزين' },
      description: {
        en: 'Provider status & broken-file re-upload',
        ar: 'حالة المزود وإعادة رفع الملفات المفقودة'
      },
      visible: isSuperAdmin || isITInf
    },
    {
      path: '/system/two-step',
      icon: Smartphone,
      label: { en: 'Two-Step Sign-In', ar: 'الدخول بخطوتين' },
      description: {
        en: 'Require a sign-in code, per role or per person',
        ar: 'إلزام رمز تحقق حسب الدور أو الشخص'
      },
      // Super Admin only: hr_manager is one of the roles this policy governs,
      // so letting it edit the policy would let it exempt itself. The backend
      // returns 403 for anyone else.
      visible: isSuperAdmin
    },
    {
      path: '/system/nda',
      icon: FileCheck2,
      label: { en: 'Confidentiality Undertaking', ar: 'تعهد السرية' },
      description: {
        en: 'Require signing at sign-in, per person or everyone',
        ar: 'إلزام التوقيع عند الدخول، لكل شخص أو للجميع'
      },
      // Super Admin only, for the same reason as Two-Step Sign-In: this policy
      // governs every role, so no role it governs may edit it.
      visible: isSuperAdmin
    },
    {
      path: '/system/id-formats',
      icon: Hash,
      label: { en: 'ID & Number Formats', ar: 'تنسيق الأرقام والهويات' },
      description: {
        en: 'How many IDs and numbers are stored as numbers, not text',
        ar: 'كم هوية ورقم محفوظ كرقم لا كنص'
      },
      visible: isSuperAdmin
    },
    {
      path: '/system/reset',
      icon: Trash2,
      label: { en: 'System Reset', ar: 'إعادة تعيين النظام' },
      description: { en: 'Factory reset / purge data', ar: 'إعادة ضبط المصنع / حذف البيانات' },
      visible: isSuperAdmin,  // Super Admin ONLY - destructive purge is never granted to HR Manager
      danger: true
    },
  ].filter(item => item.visible);
  
  return (
    <div className="flex flex-col md:flex-row min-h-[calc(100vh-120px)]">
      {/* System Sidebar - horizontal on mobile, vertical on desktop */}
      <div className={cn(
        "bg-slate-50/50 p-3 md:p-4 flex-shrink-0",
        "border-b md:border-b-0 md:w-64",
        isRTL ? "md:border-s" : "md:border-e"
      )}>
        <div className="flex items-center gap-2 px-3 py-2 mb-2 md:mb-4">
          <Shield className="h-5 w-5 text-slate-600" />
          <h2 className="font-semibold text-slate-900">
            {isRTL ? 'النظام' : 'System'}
          </h2>
        </div>
        
        {/* Mobile: horizontal scroll tabs, Desktop: vertical nav */}
        <nav className="flex md:flex-col gap-1 md:space-y-1 overflow-x-auto pb-2 md:pb-0 -mx-3 px-3 md:mx-0 md:px-0">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => cn(
                "flex items-center gap-2 md:gap-3 px-3 py-2 md:py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap md:whitespace-normal",
                isActive 
                  ? item.danger 
                    ? "bg-red-50 text-red-700 font-medium"
                    : "bg-blue-50 text-blue-700 font-medium" 
                  : item.danger
                    ? "text-red-600 hover:bg-red-50 hover:text-red-700"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              )}
            >
              <item.icon className={cn("h-4 w-4 flex-shrink-0", item.danger && "text-red-500")} />
              <div className="flex-1 min-w-0">
                <div className="truncate">
                  {isRTL ? item.label.ar : item.label.en}
                </div>
                <div className={cn(
                  "hidden md:block text-xs truncate",
                  item.danger ? "text-red-400" : "text-slate-400"
                )}>
                  {isRTL ? item.description.ar : item.description.en}
                </div>
              </div>
            </NavLink>
          ))}
        </nav>
      </div>
      
      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
};

export default SystemPage;
